import React, { useState, useEffect } from 'react'
import axios from 'axios'

const BASE_URL = "http://192.168.1.116:9001";

const resolveSrc = (value) => {
  if (!value) return null;
  if (typeof value === "string") return value.startsWith("http") ? value : `${BASE_URL}/${value}`;
  return null;
};

const getBadgeClass = (type) => {
  switch (type) {
    case "Permanent":         return "app-badge app-badge--permanent";
    case "Contract":          return "app-badge app-badge--contract";
    case "Internship / NYSC": return "app-badge app-badge--internship";
    case "Temporary":         return "app-badge app-badge--temporary";
    default:                  return "app-badge app-badge--default";
  }
};

const AdminApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("All");

  const fetchApplications = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const { data } = await axios.get(`${BASE_URL}/api/applications`);
      console.log("Raw response:", data);
      const payload = data?.data ?? data;
      const list = Array.isArray(payload) ? payload : payload?.applications ?? [];
      setApplications(list);
      console.log("Applications loaded:", list.length);
    } catch (err) {
      console.error("Failed to fetch applications:", err);
      setFetchError(err.message);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 5000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchApplications(); }, []);

  const employmentTypes = ["All", "Permanent", "Contract", "Internship / NYSC", "Temporary"];

  const filtered = applications.filter((app) => {
    const matchesType = filterType === "All" || app.positionInformation?.employmentType === filterType;
    const q = search.toLowerCase();
    const matchesSearch =
      !q ||
      app.personalDetails?.fullName?.toLowerCase().includes(q) ||
      app.positionInformation?.department?.toLowerCase().includes(q) ||
      app.personalDetails?.email?.toLowerCase().includes(q);
    return matchesType && matchesSearch;
  });

  return (
    <div>

      {/* ── PAGE HEADER ── */}
      <div className="content-wrapper">
        <div className="top-bar">
          <button className="app-refresh-btn" onClick={fetchApplications} disabled={loading} title="Refresh">
            {loading ? "..." : "↻"}
          </button>
        </div>
        <div className="text-section">
          <p className="aboutusId wow animate__animated animate__fadeInUp">
            <span className="aboutusId-link">Admin</span>
            <span className="separator">{">"}</span>
            <span className="aboutusId-link active">Applications</span>
          </p>
          <h1 className="main-headings wow animate__animated animate__fadeInUp">
            Job Applications
          </h1>
        </div>
      </div>

      {/* ── SEARCH + FILTER ── */}
      <div className="app-toolbar">
        <input
          type="text"
          className="app-search"
          placeholder="Search by name, department, email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="app-filter-tabs">
          {employmentTypes.map((type) => (
            <button
              key={type}
              className={`app-filter-tab ${filterType === type ? "active" : ""}`}
              onClick={() => setFilterType(type)}
            >
              {type}
            </button>
          ))}
        </div>
        <span className="app-count">
          {filtered.length} application{filtered.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* ── TABLE ── */}
      <div className="app-table-wrapper">
        {loading ? (
          <p className="app-empty">Loading applications...</p>
        ) : filtered.length === 0 ? (
          <p className="app-empty">
            {applications.length === 0 ? "No applications received yet." : "No results match your search."}
          </p>
        ) : (
          <table className="app-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Full Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Department</th>
                <th>Type</th>
                <th>Availability</th>
                <th>NYSC Status</th>
                <th>Technical Skills</th>
                <th>CV</th>
                <th>Cover Letter</th>
                <th>Certificates</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((app, index) => (
                <tr key={app._id || index}>
                  <td>{index + 1}</td>
                  <td className="app-td--name">{app.personalDetails?.fullName || "—"}</td>
                  <td>{app.personalDetails?.email || "—"}</td>
                  <td>{app.personalDetails?.phoneNumber || "—"}</td>
                  <td>{app.positionInformation?.department || "—"}</td>
                  <td>
                    <span className={getBadgeClass(app.positionInformation?.employmentType)}>
                      {app.positionInformation?.employmentType || "—"}
                    </span>
                  </td>
                  <td>{app.additional?.availability || "—"}</td>
                  <td>{app.nysc?.status || "—"}</td>
                  <td>{app.skills?.technicalSkills || "—"}</td>
                  <td>
                    {resolveSrc(app.documents?.cv)
                      ? <a href={resolveSrc(app.documents?.cv)} target="_blank" rel="noreferrer" className="app-cv-link">View</a>
                      : <span className="app-cv-none">—</span>}
                  </td>
                  <td>
                    {resolveSrc(app.documents?.coverLetter)
                      ? <a href={resolveSrc(app.documents?.coverLetter)} target="_blank" rel="noreferrer" className="app-cv-link">View</a>
                      : <span className="app-cv-none">—</span>}
                  </td>
                  <td>
                    {resolveSrc(app.documents?.certificates)
                      ? <a href={resolveSrc(app.documents?.certificates)} target="_blank" rel="noreferrer" className="app-cv-link">View</a>
                      : <span className="app-cv-none">—</span>}
                  </td>
                  <td>
                    <span className={`app-badge ${app.status === "pending" ? "app-badge--contract" : "app-badge--permanent"}`}>
                      {app.status || "—"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── TOAST ── */}
      {showToast && (
        <div className="em-toast em-toast--error">
          <div className="em-toast-icon">✕</div>
          <div className="em-toast-body">
            <p className="em-toast-title">Failed to load applications</p>
            <p className="em-toast-message">{fetchError}</p>
          </div>
          <button className="em-toast-close" onClick={() => setShowToast(false)}>✕</button>
        </div>
      )}

    </div>
  );
};

export default AdminApplications